import { useQuery } from '@tanstack/react-query';
import { Download } from 'lucide-react';
import { orpc } from '../../apis/orpc-query';
import { Button } from '../../components/ui/Button';
import { Dialog } from '../../components/ui/Dialog';
import { Spinner } from '../../components/ui/Spinner';
import { StatusText } from '../../components/ui/StatusText';
import { formatDayTime } from '../../lib/format';
import { useI18nTranslation } from '../../lib/use-i18n-translation';
import { batchStatusTone, errorReportCsv, errorReportFilename, saveBlob } from './import-display';

export interface BatchErrorDialogProps {
  batchId: string | null;
  onClose: () => void;
}

/**
 * One batch's error report. The rows come straight from `import.getBatch`, and the CSV export is built in the
 * browser from that same payload — nothing is re-derived, so the file a user hands to a colleague says exactly
 * what the screen said.
 */
export const BatchErrorDialog = ({ batchId, onClose }: BatchErrorDialogProps) => {
  const { t } = useI18nTranslation();

  const batch = useQuery(orpc.import.getBatch.queryOptions({ input: { id: batchId ?? '' }, enabled: batchId !== null }));
  const detail = batch.data ?? null;
  const errorRows = detail?.errorReport ?? [];

  return (
    <Dialog
      isOpen={batchId !== null}
      onClose={onClose}
      title={detail?.filename ?? t('import.history.batch')}
      description={
        detail ? t('import.history.dialogSubtitle', { date: formatDayTime(detail.createdAt), uploader: detail.uploadedByName ?? '—' }) : undefined
      }
      className="max-w-3xl"
      footer={
        <>
          <Button
            variant="outline"
            disabled={errorRows.length === 0}
            onClick={() => saveBlob(new Blob([errorReportCsv(errorRows)], { type: 'text/csv' }), errorReportFilename(detail?.filename ?? 'import'))}
          >
            <Download className="size-4" />
            {t('import.history.downloadErrors')}
          </Button>
          <Button onClick={onClose}>{t('actions.close')}</Button>
        </>
      }
    >
      {batch.isPending ? (
        <p className="flex items-center gap-2 text-sm text-muted-foreground">
          <Spinner />
          {t('table.loading')}
        </p>
      ) : batch.isError || !detail ? (
        <p
          role="alert"
          className="text-sm text-destructive"
        >
          {t('errors.loadFailed')}
        </p>
      ) : (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-x-6 gap-y-1 text-sm">
            <span className="text-muted-foreground">
              {t('import.history.counts', { rows: detail.rowCount, accepted: detail.acceptedCount, rejected: detail.rejectedCount })}
            </span>
            <StatusText
              label={t(`import.batchStatus.${detail.status}`)}
              tone={batchStatusTone(detail.status)}
            />
          </div>

          {errorRows.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t('import.history.noErrors')}</p>
          ) : (
            <div className="max-h-96 overflow-y-auto rounded-md border border-border">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="border-b border-border">
                    <th
                      scope="col"
                      className="whitespace-nowrap px-3 py-2 text-start text-xs font-medium uppercase tracking-wide text-muted-foreground"
                    >
                      {t('import.columns.row')}
                    </th>
                    <th
                      scope="col"
                      className="whitespace-nowrap px-3 py-2 text-start text-xs font-medium uppercase tracking-wide text-muted-foreground"
                    >
                      {t('import.columns.code')}
                    </th>
                    <th
                      scope="col"
                      className="px-3 py-2 text-start text-xs font-medium uppercase tracking-wide text-muted-foreground"
                    >
                      {t('import.columns.reason')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {errorRows.map((row) => (
                    <tr
                      key={`${row.rowNumber}-${row.code ?? ''}`}
                      className="border-b border-border last:border-b-0"
                    >
                      <td className="whitespace-nowrap px-3 py-2 align-top text-foreground">{row.rowNumber}</td>
                      <td className="whitespace-nowrap px-3 py-2 align-top font-mono text-xs text-muted-foreground">{row.code ?? '—'}</td>
                      <td className="px-3 py-2 align-top text-foreground">{row.reason}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </Dialog>
  );
};
